import { z } from 'zod';
import { IsoTimestamp, RunId } from './common';

/**
 * Failure-snapshot capture protocol.
 *
 * When the worker hits an apply failure (form-not-detected, submit-button-
 * missing, captcha after Easy-Apply success-detection, ATS error page, etc.)
 * we capture the diagnostic bundle:
 *
 *   screenshot.png   — full-page PNG of the moment of failure
 *   dom.html         — outerHTML of the document at the same moment
 *   console.log      — Playwright-captured browser console messages
 *   meta.json        — { runId, linkedinJobId, atsHost, lastAction, error }
 *
 * The worker requests presigned PUT URLs from the cloud, uploads each
 * file directly to S3, and references the resulting key prefix on the
 * Application row. Closes the loop on the previous personal-use behavior
 * of "leave the tab open and look at it" — adapted for headless.
 *
 * Cloud-side lifecycle: snapshots auto-expire after 30 days via S3 Lifecycle.
 */

export const SnapshotFilename = z.enum(['screenshot.png', 'dom.html', 'console.log', 'meta.json']);
export type SnapshotFilename = z.infer<typeof SnapshotFilename>;

export const CreateSnapshotUploadRequest = z.object({
  runId: RunId,
  linkedinJobId: z.string().min(1),
  /** Which files the worker plans to upload. */
  files: z.array(SnapshotFilename).min(1),
});
export type CreateSnapshotUploadRequest = z.infer<typeof CreateSnapshotUploadRequest>;

export const SnapshotUpload = z.object({
  filename: SnapshotFilename,
  uploadUrl: z.string().url(),
  s3Key: z.string(),
});
export type SnapshotUpload = z.infer<typeof SnapshotUpload>;

export const CreateSnapshotUploadResponse = z.object({
  /** All files share this prefix; persist it on the Application row. */
  s3Prefix: z.string(),
  expiresAt: IsoTimestamp,
  uploads: z.array(SnapshotUpload),
});
export type CreateSnapshotUploadResponse = z.infer<typeof CreateSnapshotUploadResponse>;
